import { FormSelectOption } from '@/core/types/form-select-option';
import { DEFAULT_HTTP_ERROR_MESSAGE } from '@/core/constants/default-http-error-message';
import { AxiosError } from 'axios';
import { AuthenticatedHttpError } from '@/core/types/authenticated-http-error';
import { AUTHENTICATED_HTTP_DEFAULT_ERROR_RESPONSE_MESSAGE } from '@/core/constants/authenticated-http-default-error-response-message';
import { AuthenticatedHttpErrorSchema } from '@/core/types/authenticated-http-error-schema';
import { AuthenticatedHttpValidatedError } from '@/core/types/authenticated-http-validated-error';

/**
 * Given an array of primitives (string or number)
 *
 * Returns an array of FormSelectOption where the value and text
 * are the string representation of each primitive
 *
 * @param primitives - (string | number)[]
 * @return FormSelectOption[]
 */
export const createFormSelectOptionsFromPrimitives = (
	primitives: (string | number)[],
): FormSelectOption[] => {
	const selectOptions = primitives.map((pr) => ({
		value: String(pr),
		text: String(pr),
	}));

	return selectOptions;
};

/**
 * Creates an HTML form element on the fly to send form POST requests
 *
 * @param fields - { [key: string]: string } - fields to process and send as form data
 * @param url - string - url to send form request to
 * @param target - string? - optional form target, default __self
 * @returns void
 */
export const sendFormPostRequest = ({
	fields,
	url,
	target,
}: {
	fields: { [key: string]: string };
	url: string;
	target?: string;
}): void => {
	const form = document.createElement('form');
	form.target = target ?? '_self';
	form.method = 'post';
	form.action = url;

	Object.keys(fields).forEach((key) => {
		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = key;
		input.value = fields[key as string];
		form.appendChild(input);
	});

	document.body.appendChild(form);

	form.submit();
	form.remove();
};

/**
 * Given an error of any type,
 * Return an appropriate error message
 *
 * If the error is not defined,
 * Return the defaultMessage,
 *
 * If the error is of type string,
 * Return the error
 *
 * If the error is of a API HTTP error type,
 * Return the error message
 *
 * @param error - any
 * @param defaultMessage - string - default equal to DEFAULT_HTTP_ERROR_MESSAGE
 * @returns string
 */
export const getHttpErrorMessage = (error?: any, defaultMessage = DEFAULT_HTTP_ERROR_MESSAGE) => {
	if (!error) {
		return defaultMessage;
	}

	if (typeof error === 'string') {
		return error;
	}

	if (error?.response?.data?.error?.message) {
		return error?.response?.data?.error?.message;
	}

	if (error?.message) {
		return error?.message;
	}

	return defaultMessage;
};

/**
 * Axios HTTP request middleware for error response intercepting
 *
 * @param callback - Function - called if 401 is received
 * @param errorCode - number
 * @returns Promise with AxiosError
 */
export const createAxiosErrorInterceptor = (callback: () => unknown, errorCode: number) =>
	(error: AxiosError) => {
		if (error.response?.status === errorCode) {
			callback();
		}

		return Promise.reject(error);
	};

/**
 * Returns an AuthenticatedHttpErrorResponse message for an Authenticated HTTP request that errored out.
 *
 * @remarks
 * This will look for and use an error message from the API error response
 * If there is not an error message from the API then it will use the fallBackMessage.
 * If there is not a fallBackMessage then it will return the AUTHENTICATED_HTTP_DEFAULT_ERROR_RESPONSE_MESSAGE
 *
 * @param errorResponse - AuthenticatedHttpErrorResponse
 * @param fallbackMessage - error message to fallback to
 *
 * @returns string error message
 */
export const getAuthenticatedHttpResponseErrorMessage = (
	error: AuthenticatedHttpError,
	fallbackMessage?: string,
): string => {
	const isErrorsValid = !!error.error?.errors && Array.isArray(error.error?.errors);

	if (!isErrorsValid) {
		if (fallbackMessage) {
			return fallbackMessage;
		}

		return AUTHENTICATED_HTTP_DEFAULT_ERROR_RESPONSE_MESSAGE;
	}

	const errorMessage = error.error?.errors[0]?.message;
	const isErrorMessageDefined = errorMessage && errorMessage.length > 0;

	if (isErrorMessageDefined) {
		return errorMessage;
	}

	if (fallbackMessage) {
		return fallbackMessage;
	}

	return AUTHENTICATED_HTTP_DEFAULT_ERROR_RESPONSE_MESSAGE;
};

/**
 * Validates an Axios Error response against an AuthenticatedHttpResponseError
 *
 * @remarks
 * This uses the Joi library to validate the error response
 * against the AuthenticatedHttpResponseErrorSchema
 *
 * @param error - AxiosError
 * @param fallbackMessage - error message to fallback to
 *
 * @returns AuthenticatedHttpValidatedError
 */
export const validateAxiosError = (
	error: AxiosError,
	fallbackErrorMessage?: string,
): AuthenticatedHttpValidatedError => {
	const validated = AuthenticatedHttpErrorSchema.validate(error.response?.data);
	const isValid = !validated.error;
	const response = validated.value;
	const message = getAuthenticatedHttpResponseErrorMessage(response, fallbackErrorMessage);

	return {
		schemaError: validated.error,
		isValid,
		message,
		response,
	};
};
